import { createReelJob, getReelJob } from '@/api/ghlAi.api.js';
import { publishContentCampaign, updateContentCampaign } from '@/api/ghlContent.api.js';
import { normalizeCaptionStyle } from '@/utils/captionStyleCatalog.js';

function buildReelScriptFromCampaign(content: Record<string, any> = {}) {
  const reelScript = content.reelScript;
  const script: Record<string, any> = reelScript && typeof reelScript === 'object'
    ? { ...reelScript }
    : { scenes: [] };
  const captionStyle = normalizeCaptionStyle(
    content.reelCaptionStyle || script.captionStyle || null
  );
  return {
    ...script,
    voiceover: String(script.voiceover || '').trim(),
    caption: String(content.caption || script.caption || script.voiceover || '').trim(),
    toneId: content.reelToneId || script.toneId || 'educator',
    reelType: 'business',
    product: null,
    captionStyle
  };
}

export async function waitForReelJob(jobId: number | string, { intervalMs = 3000, maxWaitMs = 600000 } = {}) {
  const normalizedId = Number(jobId);
  if (!normalizedId) {
    throw new Error('Video generation did not start.');
  }
  const started = Date.now();
  while (Date.now() - started < maxWaitMs) {
    const response = await getReelJob(normalizedId);
    const job = response.data || {};
    if (job.status === 'ready') {
      return job;
    }
    if (job.status === 'failed') {
      throw new Error(job.error || 'Video generation failed.');
    }
    await new Promise((resolve) => {
      setTimeout(resolve, intervalMs);
    });
  }
  throw new Error('Video generation timed out. Please try again.');
}

export async function publishSeriesDraftCampaign({
  campaign,
  locationId
}: {
  campaign: { id: number; content?: Record<string, unknown>; platforms?: string[]; scheduledAt?: string };
  locationId?: string;
}) {
  const content = campaign.content || {};
  const script = buildReelScriptFromCampaign(content);
  if (!script.voiceover) {
    throw new Error('Add a voiceover before publishing.');
  }

  const jobPayload: Record<string, unknown> = {
    voiceId: content.reelVoiceId,
    toneId: content.reelToneId || script.toneId,
    locationId: locationId || undefined,
    campaignId: campaign.id,
    reelType: 'business',
    script
  };
  if (content.reelMusicTrackId) {
    jobPayload.musicTrackId = content.reelMusicTrackId;
  }

  const jobResponse = await createReelJob(jobPayload);
  const data = jobResponse.data || {};
  const jobId = Number((data as { jobId?: unknown }).jobId || 0);
  const job = await waitForReelJob(jobId);
  const publicUrl = String(job.publicUrl || '').trim();
  if (!publicUrl) {
    throw new Error('Video is not ready yet. Please try again.');
  }

  const payload = {
    content: {
      ...content,
      caption: script.caption,
      mediaUrls: [publicUrl],
      reelJobId: jobId,
      reelCaptionStyle: script.captionStyle,
      reelScript: script,
      facebookPostType: 'reel',
      reelType: 'business'
    },
    platforms: campaign.platforms || [],
    scheduledAt: campaign.scheduledAt || undefined,
    publishNow: false
  };

  await updateContentCampaign(campaign.id, payload, []);
  await publishContentCampaign(campaign.id);
}
